import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_URL } from '../context/AuthContext';
import { generarBoletin } from './generarBoletin';

const CATEGORIAS = ['Writing', 'Listening', 'Reading', 'Speaking'];
const CAT_ICONS = { Writing: '✍️', Listening: '👂', Reading: '📖', Speaking: '🗣️' };

const scoreColor = (s) => s === null ? '#BE84A3' : s >= 85 ? '#8B5CF6' : s >= 70 ? '#22C55E' : s >= 50 ? '#F59E0B' : '#EF4444';
const scoreBg   = (s) => s === null ? '#F9F0F5' : s >= 85 ? '#8B5CF610' : s >= 70 ? '#22C55E10' : s >= 50 ? '#F59E0B10' : '#EF444410';
const scoreLabel = (s) => s === null ? '—' : s >= 85 ? 'Excelente' : s >= 70 ? 'Bueno' : s >= 50 ? 'Aprobado' : 'Suspenso';

function calcStats(intentos, speakingNotas = []) {
  const total = intentos.length;
  const avgGlobal = total > 0 ? Math.round(intentos.reduce((s, i) => s + i.puntuacion, 0) / total) : null;

  const porCategoria = {};
  ['Writing', 'Listening', 'Reading'].forEach(cat => {
    const ci = intentos.filter(i => i.categoria === cat);
    if (ci.length === 0) {
      porCategoria[cat] = { total: 0, avg: null, ultimo: null, tendencia: null };
    } else {
      const avg = Math.round(ci.reduce((s, i) => s + i.puntuacion, 0) / ci.length);
      const ultimo = ci[ci.length - 1].puntuacion;
      let tendencia = null;
      if (ci.length > 1) {
        const prev = ci[ci.length - 2].puntuacion;
        tendencia = ultimo > prev ? '↑' : ultimo < prev ? '↓' : '→';
      }
      porCategoria[cat] = { total: ci.length, avg, ultimo, tendencia };
    }
  });

  // Speaking: nota directa del profesor (0-10), convertida a 0-100 para colores
  const sn = speakingNotas.length > 0 ? speakingNotas[speakingNotas.length - 1] : null;
  if (!sn) {
    porCategoria['Speaking'] = { total: 0, avg: null, notaRaw: null, ultimo: null, tendencia: null, esSpeaking: true };
  } else {
    const notaRaw = sn.nota;
    const avg = Math.round(notaRaw * 10);
    porCategoria['Speaking'] = { total: 1, avg, notaRaw, ultimo: avg, tendencia: null, esSpeaking: true };
  }

  return { total, avgGlobal, porCategoria };
}

export default function Aprendizaje() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [alumnos, setAlumnos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);
  const [nivelActivo, setNivelActivo] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [modalBoletin, setModalBoletin] = useState(false);
  const [comentarioProfesor, setComentarioProfesor] = useState('');

  useEffect(() => {
    if (!['profesor', 'admin'].includes(usuario?.rol)) { navigate('/'); return; }
    axios.get(`${API_URL}/aprendizaje`)
      .then(r => setAlumnos(r.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  const alumnosFiltrados = alumnos.filter(a => {
    const matchBusqueda = a.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
                          a.email.toLowerCase().includes(busqueda.toLowerCase());
    const matchTipo = filtroTipo === 'todos' || (a.accesos || []).some(ac => ac.tipo === filtroTipo);
    return matchBusqueda && matchTipo;
  });

  const alumnoActivo = seleccionado ? alumnos.find(a => a.id === seleccionado) : null;

  // Pestañas de nivel: todos los accesos del alumno + niveles que tienen intentos aunque no tengan acceso activo
  const nivelesDisponibles = alumnoActivo ? (() => {
    const tabs = new Map();
    // Primero añadir accesos registrados
    (alumnoActivo.accesos || []).forEach(a => tabs.set(`${a.tipo}|${a.nivel}`, { tipo: a.tipo, nivel: a.nivel }));
    // Añadir niveles de intentos que no estén ya en accesos
    (alumnoActivo.intentos || []).forEach(i => {
      const k = `${i.eval_tipo}|${i.eval_nivel}`;
      if (!tabs.has(k)) tabs.set(k, { tipo: i.eval_tipo, nivel: i.eval_nivel });
    });
    return [...tabs.values()];
  })() : [];

  // Cuando cambia el alumno, seleccionar el primer nivel disponible
  useEffect(() => {
    if (nivelesDisponibles.length > 0) {
      setNivelActivo(`${nivelesDisponibles[0].tipo}|${nivelesDisponibles[0].nivel}`);
    } else {
      setNivelActivo(null);
    }
  }, [seleccionado]);

  const nivelActivoObj = nivelActivo ? { tipo: nivelActivo.split('|')[0], nivel: nivelActivo.split('|')[1] } : null;

  const intentosFiltrados = alumnoActivo && nivelActivo
    ? alumnoActivo.intentos.filter(i => `${i.eval_tipo}|${i.eval_nivel}` === nivelActivo)
    : [];

  const speakingFiltradas = alumnoActivo && nivelActivo
    ? (alumnoActivo.speaking_notas || []).filter(sn => `${sn.eval_tipo}|${sn.eval_nivel}` === nivelActivo)
    : [];

  const stats = calcStats(intentosFiltrados, speakingFiltradas);

  const S = {
    page:   { minHeight: '100vh', background: '#FFF0F5', display: 'flex', flexDirection: 'column' },
    nav:    { background: '#FFFFFF', borderBottom: '1px solid #FBCFE8', padding: '0 24px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(236,72,153,0.08)', flexShrink: 0 },
    input:  { padding: '8px 12px', borderRadius: '8px', border: '1px solid #FBCFE8', background: '#FFF0F5', color: '#581C47', fontSize: '13px', outline: 'none', width: '100%' },
    select: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #FBCFE8', background: '#FFF0F5', color: '#581C47', fontSize: '13px', outline: 'none' },
  };

  return (
    <div style={S.page}>
      <nav style={S.nav}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>📊</span>
          <span style={{ color: '#581C47', fontWeight: 700, fontSize: '16px' }}>Aprendizaje</span>
          <span style={{ background: '#FCE7F3', color: '#EC4899', border: '1px solid #FBCFE8', borderRadius: '6px', padding: '2px 10px', fontSize: '11px', fontWeight: 700 }}>PROFESOR</span>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/seleccion')} style={{ background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            ← Volver
          </button>
          <button onClick={() => { logout(); navigate('/'); }} style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', padding: '7px 18px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Cerrar sesión
          </button>
        </div>
      </nav>

      <div className="aprendizaje-layout">
        {/* Panel izquierdo */}
        <aside className={`aprendizaje-panel${seleccionado ? ' has-selection' : ''}`}>
          <div style={{ padding: '16px', borderBottom: '1px solid #FBCFE8' }}>
            <p style={{ color: '#BE84A3', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', fontWeight: 600 }}>
              Alumnos ({alumnosFiltrados.length})
            </p>
            <input style={{ ...S.input, marginBottom: '8px' }} placeholder="Buscar alumno..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
            <select style={{ ...S.select, width: '100%' }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
              <option value="todos">Todos los tipos</option>
              <option value="militar">Militar</option>
              <option value="civil">Civil</option>
            </select>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {cargando ? (
              <div style={{ color: '#BE84A3', padding: '32px', textAlign: 'center', fontSize: '13px' }}>Cargando...</div>
            ) : alumnosFiltrados.length === 0 ? (
              <div style={{ color: '#BE84A3', padding: '32px', textAlign: 'center', fontSize: '13px' }}>No hay alumnos</div>
            ) : alumnosFiltrados.map(a => {
              const st = calcStats(a.intentos, a.speaking_notas || []);
              const activo = seleccionado === a.id;
              return (
                <div key={a.id} onClick={() => setSeleccionado(a.id)} style={{ padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #FCE7F3', background: activo ? '#FFF0F5' : '#FFFFFF', borderLeft: activo ? '3px solid #EC4899' : '3px solid transparent', transition: 'all 0.15s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>
                      {a.nombre.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: '#581C47', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.nombre}</p>
                      <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                        {(a.accesos || []).map(ac => (
                          <span key={`${ac.tipo}-${ac.nivel}`} style={{ background: ac.tipo === 'militar' ? '#EDE9FE' : '#FCE7F3', color: ac.tipo === 'militar' ? '#7C3AED' : '#EC4899', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: 600 }}>
                            {ac.nivel}
                          </span>
                        ))}
                        {(a.accesos || []).length === 0 && (
                          <span style={{ color: '#BE84A3', fontSize: '10px' }}>Sin accesos</span>
                        )}
                      </div>
                    </div>
                    {st.avgGlobal !== null ? (
                      <div style={{ textAlign: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: scoreColor(st.avgGlobal), fontFamily: 'Inter, sans-serif' }}>{st.avgGlobal}%</div>
                        <div style={{ fontSize: '10px', color: '#BE84A3' }}>{st.total} ex.</div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '11px', color: '#BE84A3' }}>Sin datos</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Panel derecho */}
        <main className="aprendizaje-main">
          {!alumnoActivo ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
              <div style={{ fontSize: '56px' }}>👈</div>
              <p style={{ color: '#BE84A3', fontSize: '16px' }}>Selecciona un alumno para ver su evolución</p>
            </div>
          ) : (
            <div className="fade-in">
              {/* Cabecera alumno */}
              <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '16px', padding: '24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 2px 12px rgba(236,72,153,0.06)', position: 'relative' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '24px', flexShrink: 0, boxShadow: '0 4px 14px rgba(236,72,153,0.35)' }}>
                  {alumnoActivo.nombre.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ color: '#581C47', fontSize: '22px', fontWeight: 700, marginBottom: '4px', fontFamily: 'Inter, sans-serif' }}>{alumnoActivo.nombre}</h2>
                  <p style={{ color: '#BE84A3', fontSize: '13px', marginBottom: '8px' }}>{alumnoActivo.email}</p>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {(alumnoActivo.accesos || []).map(ac => (
                      <span key={`${ac.tipo}-${ac.nivel}`} style={{ background: ac.tipo === 'militar' ? '#EDE9FE' : '#FCE7F3', color: ac.tipo === 'militar' ? '#7C3AED' : '#DB2777', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 600 }}>
                        {ac.tipo === 'militar' ? '🎖️' : '🎓'} {ac.tipo === 'militar' ? 'Militar' : 'Civil'} {ac.nivel}
                      </span>
                    ))}
                    <span style={{ background: alumnoActivo.suscripcion_activa ? '#22C55E10' : '#EF444410', color: alumnoActivo.suscripcion_activa ? '#22C55E' : '#EF4444', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 600 }}>
                      {alumnoActivo.suscripcion_activa ? '✓ Activo' : '✕ Inactivo'}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end', flexShrink: 0 }}>
                  {stats.avgGlobal !== null && (
                    <div style={{ textAlign: 'center', padding: '16px 24px', background: scoreBg(stats.avgGlobal), borderRadius: '12px', border: `1px solid ${scoreColor(stats.avgGlobal)}22` }}>
                      <div style={{ fontSize: '36px', fontWeight: 900, color: scoreColor(stats.avgGlobal), fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{stats.avgGlobal}%</div>
                      <div style={{ color: scoreColor(stats.avgGlobal), fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>{scoreLabel(stats.avgGlobal)}</div>
                      <div style={{ color: '#BE84A3', fontSize: '11px', marginTop: '2px' }}>{stats.total} eval{stats.total !== 1 ? 's' : ''}.</div>
                    </div>
                  )}
                  {nivelActivo && (
                    <button
                      onClick={() => { setComentarioProfesor(''); setModalBoletin(true); }}
                      style={{ background: 'linear-gradient(135deg, #581C47 0%, #7C2D6B 100%)', border: 'none', color: '#fff', padding: '10px 18px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(88,28,71,0.4)', display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap' }}
                    >
                      📄 Generar Boletín
                    </button>
                  )}
                </div>
              </div>

              {/* Pestañas de nivel */}
              {nivelesDisponibles.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  {nivelesDisponibles.map(({ tipo, nivel }) => {
                    const key = `${tipo}|${nivel}`;
                    const activo = nivelActivo === key;
                    const nIntentosNivel = alumnoActivo.intentos.filter(i => `${i.eval_tipo}|${i.eval_nivel}` === key).length;
                    return (
                      <button
                        key={key}
                        onClick={() => setNivelActivo(key)}
                        style={{
                          padding: '8px 18px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                          border: activo ? '2px solid #EC4899' : '2px solid #FBCFE8',
                          background: activo ? 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' : '#FFFFFF',
                          color: activo ? '#fff' : '#BE84A3',
                          boxShadow: activo ? '0 4px 14px rgba(236,72,153,0.3)' : 'none',
                          transition: 'all 0.15s',
                          display: 'flex', alignItems: 'center', gap: '6px',
                        }}
                      >
                        <span>{tipo === 'militar' ? '🎖️' : '🎓'}</span>
                        <span>{tipo === 'militar' ? 'Militar' : 'Civil'} {nivel}</span>
                        <span style={{ background: activo ? 'rgba(255,255,255,0.25)' : '#FCE7F3', color: activo ? '#fff' : '#EC4899', borderRadius: '50px', padding: '1px 7px', fontSize: '11px' }}>
                          {nIntentosNivel}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Tarjetas por categoría (del nivel activo) */}
              {nivelActivo && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '20px' }}>
                    {CATEGORIAS.map(cat => {
                      const cs = stats.porCategoria[cat];
                      return (
                        <div key={cat} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '14px', padding: '18px', boxShadow: '0 2px 12px rgba(236,72,153,0.06)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '18px' }}>{CAT_ICONS[cat]}</span>
                              <span style={{ color: '#581C47', fontWeight: 700, fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>{cat}</span>
                            </div>
                            {cs.tendencia && (
                              <span style={{ fontSize: '18px', color: cs.tendencia === '↑' ? '#22C55E' : cs.tendencia === '↓' ? '#EF4444' : '#BE84A3', fontWeight: 700 }}>
                                {cs.tendencia}
                              </span>
                            )}
                          </div>
                          {cs.avg === null ? (
                            <div style={{ color: '#BE84A3', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>Sin datos</div>
                          ) : (
                            <>
                              <div style={{ fontSize: '32px', fontWeight: 900, color: scoreColor(cs.avg), fontFamily: 'Inter, sans-serif', lineHeight: 1, marginBottom: '4px' }}>
                                {cs.esSpeaking
                                  ? <>{cs.notaRaw}<span style={{ fontSize: '18px' }}>/10</span></>
                                  : `${cs.avg}%`}
                              </div>
                              <div style={{ fontSize: '11px', color: scoreColor(cs.avg), fontWeight: 600, marginBottom: '10px' }}>{scoreLabel(cs.avg)}</div>
                              <div style={{ background: '#FCE7F3', borderRadius: '50px', height: '6px', marginBottom: '10px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${cs.avg}%`, background: `linear-gradient(90deg, ${scoreColor(cs.avg)}, ${scoreColor(cs.avg)}aa)`, borderRadius: '50px', transition: 'width 0.6s ease' }} />
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#BE84A3' }}>
                                {cs.esSpeaking
                                  ? <span>Nota del profesor</span>
                                  : <>
                                      <span>{cs.total} examen{cs.total !== 1 ? 'es' : ''}</span>
                                      {cs.ultimo !== null && <span>Último: <b style={{ color: scoreColor(cs.ultimo) }}>{cs.ultimo}%</b></span>}
                                    </>}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Historial del nivel activo */}
                  <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(236,72,153,0.06)' }}>
                    <div style={{ padding: '18px 24px', borderBottom: '1px solid #FBCFE8', background: '#FFF8FC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{ color: '#581C47', fontSize: '16px', fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                        📋 Historial — {nivelActivoObj?.tipo === 'militar' ? '🎖️ Militar' : '🎓 Civil'} {nivelActivoObj?.nivel}
                      </h3>
                      <span style={{ color: '#BE84A3', fontSize: '13px' }}>{intentosFiltrados.length} evaluación{intentosFiltrados.length !== 1 ? 'es' : ''}</span>
                    </div>

                    {intentosFiltrados.length === 0 ? (
                      <div style={{ padding: '48px', textAlign: 'center', color: '#BE84A3' }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>📝</div>
                        <p>Sin evaluaciones realizadas en este nivel aún</p>
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#FFF0F5', borderBottom: '1px solid #FBCFE8' }}>
                            {['Fecha', 'Evaluación', 'Categoría', 'Puntuación', 'vs media cat.'].map(h => (
                              <th key={h} style={{ padding: '12px 18px', color: '#BE84A3', fontSize: '11px', textAlign: 'left', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...intentosFiltrados].reverse().map((intento, idx) => {
                            const catStats = stats.porCategoria[intento.categoria];
                            const vsMedia = catStats?.avg !== null && catStats?.avg !== undefined && intento.categoria
                              ? intento.puntuacion - catStats.avg
                              : null;
                            return (
                              <tr key={intento.id} style={{ borderBottom: '1px solid #FCE7F3', background: idx % 2 === 0 ? '#FFFFFF' : '#FFFBFD' }}>
                                <td style={{ padding: '13px 18px', color: '#BE84A3', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                  {new Date(intento.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </td>
                                <td style={{ padding: '13px 18px', color: '#581C47', fontSize: '14px', fontWeight: 500 }}>
                                  {intento.evaluacion_titulo}
                                </td>
                                <td style={{ padding: '13px 18px' }}>
                                  {intento.categoria ? (
                                    <span style={{ background: '#F3F0FF', color: '#8B5CF6', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 600 }}>
                                      {CAT_ICONS[intento.categoria]} {intento.categoria}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#BE84A3', fontSize: '12px' }}>—</span>
                                  )}
                                </td>
                                <td style={{ padding: '13px 18px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ background: scoreBg(intento.puntuacion), color: scoreColor(intento.puntuacion), borderRadius: '6px', padding: '4px 12px', fontSize: '14px', fontWeight: 700, fontFamily: 'Inter, sans-serif', minWidth: '52px', textAlign: 'center' }}>
                                      {intento.puntuacion}%
                                    </span>
                                    <div style={{ background: '#FCE7F3', borderRadius: '50px', height: '5px', width: '70px', overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${intento.puntuacion}%`, background: scoreColor(intento.puntuacion), borderRadius: '50px' }} />
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '13px 18px' }}>
                                  {vsMedia !== null ? (
                                    <span style={{ color: vsMedia > 0 ? '#22C55E' : vsMedia < 0 ? '#EF4444' : '#BE84A3', fontSize: '13px', fontWeight: 600 }}>
                                      {vsMedia > 0 ? `+${vsMedia}` : vsMedia}%
                                    </span>
                                  ) : (
                                    <span style={{ color: '#BE84A3', fontSize: '12px' }}>—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </main>
      </div>
      {/* Modal boletín */}
      {modalBoletin && alumnoActivo && nivelActivoObj && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(88,28,71,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#FFFFFF', borderRadius: '20px', padding: '32px', width: '520px', maxWidth: '95vw', boxShadow: '0 24px 64px rgba(88,28,71,0.35)', border: '1px solid #FBCFE8' }}>
            {/* Header modal */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #581C47 0%, #7C2D6B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', boxShadow: '0 4px 14px rgba(88,28,71,0.35)' }}>
                📄
              </div>
              <div>
                <h3 style={{ color: '#581C47', fontSize: '18px', fontWeight: 700, marginBottom: '2px', fontFamily: 'Inter, sans-serif' }}>Generar Boletín de Notas</h3>
                <p style={{ color: '#BE84A3', fontSize: '13px' }}>{alumnoActivo.nombre} · {nivelActivoObj.tipo === 'militar' ? 'Militar' : 'Civil'} {nivelActivoObj.nivel}</p>
              </div>
            </div>

            {/* Resumen estadístico */}
            <div style={{ background: '#FFF0F5', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              {['Writing', 'Listening', 'Reading', 'Speaking'].map(cat => {
                const cs = stats.porCategoria[cat];
                return (
                  <div key={cat} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', marginBottom: '3px' }}>
                      {cat === 'Writing' ? '✍️' : cat === 'Listening' ? '👂' : cat === 'Reading' ? '📖' : '🗣️'}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: cs.avg !== null ? scoreColor(cs.avg) : '#BE84A3', fontFamily: 'Inter, sans-serif' }}>
                      {cs.avg !== null ? `${cs.avg}%` : '—'}
                    </div>
                    <div style={{ fontSize: '10px', color: '#BE84A3' }}>{cat}</div>
                  </div>
                );
              })}
            </div>

            {/* Campo observaciones */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#581C47', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                Observaciones para el alumno <span style={{ color: '#BE84A3', fontWeight: 400 }}>(opcional)</span>
              </label>
              <textarea
                value={comentarioProfesor}
                onChange={e => setComentarioProfesor(e.target.value)}
                placeholder="Escribe aquí tus comentarios, recomendaciones o palabras de motivación para el alumno. Aparecerán en el boletín."
                rows={5}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #FBCFE8', background: '#FFF8FC', color: '#581C47', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box' }}
              />
            </div>

            {/* Botones */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  generarBoletin(alumnoActivo, nivelActivoObj, intentosFiltrados, stats, comentarioProfesor);
                  setModalBoletin(false);
                }}
                style={{ flex: 1, background: 'linear-gradient(135deg, #581C47 0%, #EC4899 100%)', border: 'none', color: '#fff', padding: '12px', borderRadius: '50px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(236,72,153,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                📥 Descargar PDF
              </button>
              <button
                onClick={() => setModalBoletin(false)}
                style={{ padding: '12px 22px', borderRadius: '50px', border: '1px solid #FBCFE8', background: '#FFF0F5', color: '#BE84A3', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
